// Uppläsning av kapiteltexten med Web Speech API (talsyntes i webbläsaren —
// ingen extern tjänst, inga nätverksanrop, rösterna följer med elevens system).
//
// Motorn läser EN MENING I TAGET som separata utterances. Det är inte bara för
// markeringen: Chrome har en gammal bugg där långa sammanhängande uppläsningar
// tystnar efter ~15 sekunder, och meningskedjan är den etablerade lösningen.
// Den aktuella meningen markeras med CSS Custom Highlight API (inga DOM-
// ändringar — viktigt eftersom texten ägs av React) och blocket den står i får
// en klass som reserv för webbläsare utan Highlight-stöd.
import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

export type UpplasningsStatus = 'stilla' | 'laser' | 'pausad'

export const HASTIGHETER = [0.8, 1, 1.2, 1.5] as const

interface Mening {
  block: HTMLElement
  start: number
  slut: number
}

/** Blocken som läses, i dokumentordning. Bara lövblock — i en punktlista där
 *  varje punkt är ett eget stycke (li > p) läses stycket, inte hela punkten
 *  en gång till. */
const BLOCK_VALJARE = 'h2, h3, p, li'

/** Meningsgränser: skiljetecken följt av blanktecken och versal/siffra/citat.
 *  Kravet på versal efter gör att svenska förkortningar (t.ex., bl.a., dvs.)
 *  inte klipper meningen mitt itu. */
function delaIMeningar(text: string): [number, number][] {
  const granser: number[] = []
  const re = /[.!?…]+/g
  let traff: RegExpExecArray | null
  while ((traff = re.exec(text))) {
    const efter = text.slice(traff.index + traff[0].length).trimStart()
    if (efter === '' || /^[A-ZÅÄÖ0-9"”»(]/.test(efter)) granser.push(traff.index + traff[0].length)
  }
  const meningar: [number, number][] = []
  let start = 0
  for (const grans of [...granser, text.length]) {
    if (grans <= start) continue
    const bit = text.slice(start, grans)
    const vansterTrim = bit.length - bit.trimStart().length
    const hogerTrim = bit.length - bit.trimEnd().length
    if (bit.trim()) meningar.push([start + vansterTrim, grans - hogerTrim])
    start = grans
  }
  return meningar
}

function samlaMeningar(container: HTMLElement): Mening[] {
  const alla = [...container.querySelectorAll<HTMLElement>(BLOCK_VALJARE)]
  const lovblock = alla.filter((el) => !el.querySelector(BLOCK_VALJARE))
  return lovblock.flatMap((block) =>
    delaIMeningar(block.textContent ?? '').map(([start, slut]) => ({ block, start, slut })),
  )
}

/** Bygger en DOM-range för teckenintervallet [start, slut) i blockets
 *  textinnehåll, över elementgränser (fetstil, begreppsmarkeringar m.m.). */
function rangeFor(mening: Mening): Range | null {
  const vandrare = document.createTreeWalker(mening.block, NodeFilter.SHOW_TEXT)
  const range = document.createRange()
  let position = 0
  let startSatt = false
  let nod: Node | null
  while ((nod = vandrare.nextNode())) {
    const langd = nod.textContent?.length ?? 0
    if (!startSatt && mening.start < position + langd) {
      range.setStart(nod, mening.start - position)
      startSatt = true
    }
    if (startSatt && mening.slut <= position + langd) {
      range.setEnd(nod, mening.slut - position)
      return range
    }
    position += langd
  }
  return null
}

function valjRost(): SpeechSynthesisVoice | null {
  const svenska = speechSynthesis.getVoices().filter((r) => r.lang.toLowerCase().startsWith('sv'))
  // Lokala röster startar snabbare och fungerar utan nät.
  return svenska.find((r) => r.localService) ?? svenska[0] ?? null
}

export function useUpplasning(malRef: RefObject<HTMLElement | null>) {
  const [status, setStatus] = useState<UpplasningsStatus>('stilla')
  const [hastighet, setHastighet] = useState<number>(() => {
    if (typeof window === 'undefined') return 1
    const sparad = Number(window.localStorage.getItem('lyssna-hastighet'))
    return HASTIGHETER.includes(sparad as (typeof HASTIGHETER)[number]) ? sparad : 1
  })
  // Allt föränderligt bor i en ref så att utterance-callbackerna aldrig läser
  // inaktuellt state. Generationsräknaren gör gamla callbacks ofarliga efter
  // stopp/omstart (cancel() avfyrar deras onend/onerror asynkront).
  const kor = useRef({ generation: 0, meningar: [] as Mening[], index: 0, hastighet })
  kor.current.hastighet = hastighet

  const stods = typeof window !== 'undefined' && 'speechSynthesis' in window

  const rensaMarkering = useCallback(() => {
    if (typeof CSS !== 'undefined' && 'highlights' in CSS) CSS.highlights.delete('upplasning')
    document.querySelectorAll('.upplasning-block').forEach((el) => el.classList.remove('upplasning-block'))
  }, [])

  const markera = useCallback((mening: Mening) => {
    document.querySelectorAll('.upplasning-block').forEach((el) => el.classList.remove('upplasning-block'))
    mening.block.classList.add('upplasning-block')
    if (typeof CSS !== 'undefined' && 'highlights' in CSS) {
      const range = rangeFor(mening)
      if (range) CSS.highlights.set('upplasning', new Highlight(range))
    }
    // Följ med i texten — men rulla inte om blocket är dolt (annan flik).
    if (mening.block.offsetParent) {
      const rekt = mening.block.getBoundingClientRect()
      if (rekt.top < 80 || rekt.bottom > window.innerHeight - 80) {
        const lugnt = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        mening.block.scrollIntoView({ block: 'center', behavior: lugnt ? 'auto' : 'smooth' })
      }
    }
  }, [])

  const stoppa = useCallback(() => {
    kor.current.generation++
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      // resume() före cancel(): Chrome ignorerar annars ibland cancel för en
      // pausad uppläsning, som då återupptas som en ostoppbar zombie.
      speechSynthesis.resume()
      speechSynthesis.cancel()
    }
    rensaMarkering()
    setStatus('stilla')
  }, [rensaMarkering])

  const talMening = useCallback(
    (generation: number, index: number) => {
      const k = kor.current
      if (generation !== k.generation) return
      if (index >= k.meningar.length) {
        stoppa()
        return
      }
      k.index = index
      const mening = k.meningar[index]
      markera(mening)
      const yttrande = new SpeechSynthesisUtterance((mening.block.textContent ?? '').slice(mening.start, mening.slut))
      yttrande.lang = 'sv-SE'
      const rost = valjRost()
      if (rost) yttrande.voice = rost
      yttrande.rate = k.hastighet
      // Vissa webbläsare avfyrar BÅDE onend och onerror för samma yttrande
      // (särskilt vid cancel, där en avbruten mening kan ge onend i stället
      // för onerror). Utan spärren går kedjan vidare två gånger — två
      // parallella uppläsningar som talar i munnen på varandra.
      let hanterad = false
      const vidare = () => {
        if (hanterad) return
        hanterad = true
        talMening(generation, index + 1)
      }
      yttrande.onend = vidare
      yttrande.onerror = (h) => {
        // cancel()/nytt tal avfyrar interrupted/canceled — de är väntade
        // och får inte driva kedjan vidare.
        if (h.error === 'interrupted' || h.error === 'canceled') {
          hanterad = true
          return
        }
        vidare()
      }
      speechSynthesis.speak(yttrande)
    },
    [markera, stoppa],
  )

  const starta = useCallback(() => {
    if (!stods || !malRef.current) return
    speechSynthesis.resume()
    speechSynthesis.cancel()
    speechSynthesis.getVoices() // väcker den asynkrona röstlistan (Safari/iOS)
    const k = kor.current
    k.generation++
    const generation = k.generation
    k.meningar = samlaMeningar(malRef.current)
    if (!k.meningar.length) return
    setStatus('laser')
    // speak() direkt efter cancel() medan motorn ännu talar kan på macOS
    // lämna en frikopplad OS-röst som inte går att tysta (den överlever till
    // och med att fliken stängs). Vänta in tystnaden innan det nya talet.
    const start = Date.now()
    const nastaForsok = () => {
      if (generation !== kor.current.generation) return
      if ((speechSynthesis.speaking || speechSynthesis.pending) && Date.now() - start < 1000) {
        window.setTimeout(nastaForsok, 50)
        return
      }
      talMening(generation, 0)
    }
    nastaForsok()
  }, [stods, malRef, talMening])

  const pausa = useCallback(() => {
    speechSynthesis.pause()
    setStatus('pausad')
  }, [])

  const fortsatt = useCallback(() => {
    speechSynthesis.resume()
    setStatus('laser')
  }, [])

  const cyklaHastighet = useCallback(() => {
    const k = kor.current
    const nasta = HASTIGHETER[(HASTIGHETER.indexOf(k.hastighet as (typeof HASTIGHETER)[number]) + 1) % HASTIGHETER.length]
    setHastighet(nasta)
    k.hastighet = nasta
    window.localStorage.setItem('lyssna-hastighet', String(nasta))
    // Nya takten gäller från NÄSTA mening — med flit. Att avbryta den
    // pågående meningen och ta om den (cancel + speak) startade två
    // parallella uppläsningar: den avbrutna meningens onend drev den gamla
    // kedjan vidare samtidigt som omtaget startade en ny, och på macOS blev
    // den ena en OS-röst som inte gick att tysta. Meningarna är korta —
    // takten slår igenom inom några sekunder ändå.
  }, [])

  // Städa vid avmontering — annars fortsätter rösten in på nästa sida.
  useEffect(() => stoppa, [stoppa])

  // Tysta när sidan lämnas eller fliken stängs: webbläsare stoppar inte
  // talsyntesen av sig själva vid stängning i alla lägen.
  useEffect(() => {
    if (!stods) return
    const tystna = () => {
      speechSynthesis.resume()
      speechSynthesis.cancel()
    }
    window.addEventListener('pagehide', tystna)
    return () => window.removeEventListener('pagehide', tystna)
  }, [stods])

  return { status, stods, hastighet, starta, pausa, fortsatt, stoppa, cyklaHastighet }
}
